import { z } from "zod";
import { IsoDateTimeSchema, StableIdSchema } from "./common.js";

export const ModelCandidateIdSchema = StableIdSchema;

const DisplayNameSchema = z.string().trim().min(1).max(80);
const ProviderSchema = z
  .string()
  .trim()
  .min(1)
  .max(80)
  .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]*$/, "Provider 只能包含字母、数字、点、下划线、冒号或连字符");
const ModelIdSchema = z.string().trim().min(1).max(240);
const BaseUrlSchema = z
  .string()
  .trim()
  .max(512)
  .refine((value) => {
    if (!value) return true;
    try {
      const url = new URL(value);
      return (
        ["http:", "https:"].includes(url.protocol) && !url.username && !url.password && !url.hash
      );
    } catch {
      return false;
    }
  }, "API Base URL 必须是无用户名、密码和片段的 HTTP(S) 地址");
const ContextWindowSchema = z.number().int().min(1_024).max(100_000_000).nullable();
const NoteSchema = z.string().trim().max(500);
const ApiKeySchema = z.string().min(1).max(16_384);

export const ModelTestStateSchema = z.enum(["not_tested", "passed", "failed", "stale"]);

export const ModelCandidateSchema = z
  .object({
    candidateId: ModelCandidateIdSchema,
    displayName: DisplayNameSchema,
    provider: ProviderSchema,
    model: ModelIdSchema,
    baseUrl: BaseUrlSchema.nullable(),
    contextWindow: ContextWindowSchema,
    note: NoteSchema,
    enabled: z.boolean(),
    isPrimary: z.boolean(),
    revision: z.number().int().positive(),
    secretConfigured: z.boolean(),
    secretSharedBy: z.number().int().nonnegative(),
    custom: z.boolean(),
    test: z
      .object({
        state: ModelTestStateSchema,
        message: z.string().min(1).max(240).nullable(),
        testedAt: IsoDateTimeSchema.nullable(),
        testedRevision: z.number().int().positive().nullable(),
      })
      .strict(),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
  })
  .strict();

export const ModelProviderOptionSchema = z
  .object({
    provider: ProviderSchema,
    label: z.string().trim().min(1).max(100),
    models: z.array(ModelIdSchema).max(300),
    authenticated: z.boolean(),
    custom: z.boolean(),
  })
  .strict();

export const ModelCatalogSchema = z
  .object({
    current: z
      .object({
        provider: ProviderSchema.nullable(),
        model: ModelIdSchema.nullable(),
        candidateId: ModelCandidateIdSchema.nullable(),
      })
      .strict(),
    candidates: z.array(ModelCandidateSchema).max(100),
    providers: z.array(ModelProviderOptionSchema).max(100),
    updatedAt: IsoDateTimeSchema,
  })
  .strict();

const CandidateFields = {
  displayName: DisplayNameSchema,
  provider: ProviderSchema,
  model: ModelIdSchema,
  baseUrl: BaseUrlSchema.transform((value) => value || null),
  contextWindow: ContextWindowSchema,
  note: NoteSchema,
  enabled: z.boolean(),
};

export const CreateModelCandidateRequestSchema = z
  .object({
    ...CandidateFields,
    apiKey: ApiKeySchema.optional(),
  })
  .strict();

export const ModelSecretMutationSchema = z.discriminatedUnion("action", [
  z.object({ action: z.literal("keep") }).strict(),
  z.object({ action: z.literal("replace"), value: ApiKeySchema }).strict(),
  z.object({ action: z.literal("remove"), confirm: z.literal("remove_model_secret") }).strict(),
]);

export const UpdateModelCandidateRequestSchema = z
  .object({
    revision: z.number().int().positive(),
    ...CandidateFields,
    secret: ModelSecretMutationSchema,
    confirmSharedSecretChange: z.boolean().default(false),
  })
  .strict();

export const DeleteModelCandidateRequestSchema = z
  .object({
    revision: z.number().int().positive(),
    confirm: z.literal("delete_model_candidate"),
  })
  .strict();

export const TestModelCandidateRequestSchema = z
  .object({
    revision: z.number().int().positive(),
    acknowledge: z.literal("test_model_candidate"),
  })
  .strict();

export const ApplyPrimaryModelRequestSchema = z
  .object({
    commandId: StableIdSchema,
    candidateId: ModelCandidateIdSchema,
    revision: z.number().int().positive(),
    confirmExpensiveModel: z.boolean().default(false),
  })
  .strict();

export const ApplyPrimaryModelResponseSchema = z
  .object({
    state: z.enum(["applied", "confirmation_required", "unknown"]),
    candidate: ModelCandidateSchema,
    message: z.string().min(1).max(500),
    appliesTo: z.literal("new_sessions"),
    idempotent: z.boolean(),
    updatedAt: IsoDateTimeSchema,
  })
  .strict();

export type ModelCandidate = z.infer<typeof ModelCandidateSchema>;
export type ModelCatalog = z.infer<typeof ModelCatalogSchema>;
export type ModelProviderOption = z.infer<typeof ModelProviderOptionSchema>;
export type CreateModelCandidateRequest = z.infer<typeof CreateModelCandidateRequestSchema>;
export type UpdateModelCandidateRequest = z.infer<typeof UpdateModelCandidateRequestSchema>;
export type DeleteModelCandidateRequest = z.infer<typeof DeleteModelCandidateRequestSchema>;
export type TestModelCandidateRequest = z.infer<typeof TestModelCandidateRequestSchema>;
export type ApplyPrimaryModelRequest = z.infer<typeof ApplyPrimaryModelRequestSchema>;
export type ApplyPrimaryModelResponse = z.infer<typeof ApplyPrimaryModelResponseSchema>;
